import { ref, readonly, computed } from 'vue';
import axios from 'axios';

const lendingRates = ref([]);
const earnVaults = ref([]);

// Per-function loading/error state to avoid clobbering when called concurrently
const lendingLoading = ref(false);
const lendingError = ref(null);
const earnLoading = ref(false);
const earnError = ref(null);

/**
 * Fetches lending and earn rates from the backend cache.
 * Backend caches are populated by scheduled jobs using the CLI bridge.
 * Falls back to direct API calls when cache is empty.
 */
export function useProtocolRates() {
    async function fetchLendingRates(token = null) {
        try {
            lendingLoading.value = true;
            lendingError.value = null;
            const params = token ? { token } : {};
            const { data } = await axios.get('/api/lend/rates', { params });
            lendingRates.value = data?.data || data || [];
            return lendingRates.value;
        } catch (e) {
            lendingError.value = e;
            console.error('[useProtocolRates] Failed to fetch lending rates:', e);
            return [];
        } finally {
            lendingLoading.value = false;
        }
    }

    async function fetchEarnVaults(token = null) {
        try {
            earnLoading.value = true;
            earnError.value = null;
            const params = token ? { token } : {};
            const { data } = await axios.get('/api/earn/vaults', { params });
            earnVaults.value = data?.data || data || [];
            return earnVaults.value;
        } catch (e) {
            earnError.value = e;
            console.error('[useProtocolRates] Failed to fetch earn vaults:', e);
            return [];
        } finally {
            earnLoading.value = false;
        }
    }

    function getBestLendRate(token) {
        const rates = lendingRates.value.filter(
            r => r.token_symbol?.toLowerCase() === token?.toLowerCase()
        );
        if (!rates.length) return null;
        return rates.reduce((best, r) =>
            (r.supply_apy > (best?.supply_apy || 0)) ? r : best, null
        );
    }

    function getBestBorrowRate(token) {
        const rates = lendingRates.value.filter(
            r => r.token_symbol?.toLowerCase() === token?.toLowerCase()
        );
        if (!rates.length) return null;
        return rates.reduce((best, r) =>
            (r.borrow_apy < (best?.borrow_apy || Infinity)) ? r : best, null
        );
    }

    return {
        fetchLendingRates,
        fetchEarnVaults,
        getBestLendRate,
        getBestBorrowRate,
        lendingRates: readonly(lendingRates),
        earnVaults: readonly(earnVaults),
        // Per-function state
        lendingLoading: readonly(lendingLoading),
        lendingError: readonly(lendingError),
        earnLoading: readonly(earnLoading),
        earnError: readonly(earnError),
        // Combined loading/error for backwards compat
        loading: computed(() => lendingLoading.value || earnLoading.value),
        error: computed(() => lendingError.value || earnError.value),
    };
}
